import React from "react";
import LegendComponent from "@components/LegendComponent";
import { resolveStatusColumn, FormatWithCommas } from "@helpers/helpers";
import { ZERO_VALUE } from "@utils/constants";
import {
  LEGEND_DATA_TAGS,
  LEGEND_DATA_COLORS,
  COLUMN_SIZES,
  POPUP_CELL_MIN_WIDTH,
  BG_CLASS_NAME,
  TEXT_COLOR,
} from "../constants";

// Run Status Column
const buildRunStatusColumn = (translate) => {
  const legendData = [
    {
      color: LEGEND_DATA_COLORS.ABORTED,
      text: translate("reconciliation-runs.legend.aborted"),
      tag: LEGEND_DATA_TAGS.ABORTED,
    },
    {
      color: LEGEND_DATA_COLORS.ROLLED_BACK,
      text: translate("reconciliation-runs.legend.rolledBack"),
      tag: LEGEND_DATA_TAGS.ROLLED_BACK,
    },
    {
      color: LEGEND_DATA_COLORS.ROLLING_BACK,
      text: translate("reconciliation-runs.legend.rollingBack"),
      tag: LEGEND_DATA_TAGS.ROLLING_BACK,
    },
  ];

  return {
    accessorKey: "ReconciliationRunStatusName",
    header: translate("reconciliation-runs.columns.RunStatus"),
    headerHelper: () => {
      return (
        <LegendComponent
          data={legendData}
          dataToHide={["ACTIVE", "DISABLED"]}
        />
      );
    },
    size: COLUMN_SIZES.RUN_STATUS,
    sortable: true,
    canFilter: true,
    hideHeader: false,
    hideCopy: true,
    isMultiple: true,
    processFilterValue: (value, row) => {
      return row.ReconciliationRunStatusTag;
    },
    processFilterKey: (value, row) => {
      return "ReconciliationRunStatusTag";
    },
    filterType: "checkbox",
    operator: "EQ",
    cell: ({ row }) => {
      return resolveStatusColumn(
        row.original.ReconciliationRunStatusName,
        row.original.ReconciliationRunStatusTag,
      );
    },
  };
};

// Stream Name Column
const buildStreamNameColumn = (translate) => {
  return {
    accessorKey: "ReconciliationStreamName",
    header: translate("reconciliation-runs.columns.ReconciliationStreamName"),
    canFilter: true,
    isMultiple: false,
    sortable: true,
    size: COLUMN_SIZES.STREAM_NAME,
    alignment: "left",
    processFilterValue: (value, row) => {
      return row.ReconciliationStreamTag;
    },
    processFilterKey: (value, row) => {
      return "ReconciliationStreamTag";
    },
    filterType: "checkbox",
    operator: "EQ",
  };
};

// Trigger Column
const buildTriggerColumn = (translate) => {
  return {
    accessorKey: "Trigger",
    header: translate("reconciliation-runs.columns.Trigger"),
    size: COLUMN_SIZES.TRIGGER,
    canFilter: false,
    isMultiple: false,
    sortable: true,
    alignment: "left",
    hideCopy: true,
    cell: ({ row }) => {
      return (
        <span
          className={`rounded-full flex items-end gap-1 w-full font-normal text-sm border border-r-10 border-transparent`}
        >
          {row.original.IsScheduleTrigger && (
            <span>{translate("reconciliation-runs.columns.Scheduled")}</span>
          )}
          {row.original.IsManualTrigger && (
            <span>{translate("reconciliation-runs.columns.Manual")}</span>
          )}
        </span>
      );
    },
  };
};

// Business Day Column
const buildBusinessDayColumn = (translate) => {
  return {
    accessorKey: "BusinessDay",
    header: translate("reconciliation-runs.columns.BusinessDay"),
    size: COLUMN_SIZES.BUSINESS_DAY,
    sortable: true,
    canPin: false,
    canFilter: true,
    filterType: "date",
  };
};

// Start Date Column
const buildStartDateColumn = (translate) => {
  return {
    accessorKey: "StartDate",
    header: translate("reconciliation-runs.columns.StartDate"),
    size: COLUMN_SIZES.DATE,
    sortable: true,
    canFilter: true,
    filterType: "date",
    splitCellValue: "T",
  };
};

// End Date Column
const buildEndDateColumn = (translate) => {
  return {
    accessorKey: "EndDate",
    header: translate("reconciliation-runs.columns.EndDate"),
    size: COLUMN_SIZES.DATE,
    sortable: true,
    canFilter: true,
    filterType: "date",
    splitCellValue: "T",
  };
};

// Matching Records Column
const buildMatchingRecordsColumn = (translate) => {
  return {
    accessorKey: "TotalMatchingRecords",
    header: translate("reconciliation-runs.columns.MatchingRecords"),
    size: COLUMN_SIZES.MATCHING_RECORDS,
    canFilter: false,
    truncate: true,
    showThousandSeperator: true,
    popupCellDisplay: (value) => {
      return (
        <div className="relative text-primary-400 font-semibold">
          {!!value && value}
        </div>
      );
    },
    popupContent: (row) => {
      const value = row.TotalMatchingRecords;
      return (
        <div className="relative ">
          <div
            className={`flex flex-col ${POPUP_CELL_MIN_WIDTH} transition-all ${BG_CLASS_NAME} ${TEXT_COLOR} text-14 rounded-lg px-[2px] py-[8px] mb-3 opacity-100`}
          >
            <div className="flex justify-between">
              <span className="font-semibold">
                {translate(
                  "reconciliation-runs.columns.TotalMatchingRecords"
                )}
              </span>
              <span className="font-semibold">{FormatWithCommas(value)}</span>
            </div>
          </div>
        </div>
      );
    },
  };
};

// Reconciled Records Column
const buildReconciledRecordsColumn = (translate) => {
  return {
    accessorKey: "TotalReconciledRecords",
    header: translate("reconciliation-runs.columns.ReconciledRecords"),
    size: COLUMN_SIZES.RECONCILED_RECORDS,
    canFilter: false,
    truncate: true,
    showThousandSeperator: true,
    popupCellDisplay: (value) => {
      return (
        <div className="relative text-primary-400 font-semibold">
          {!!value && value}
        </div>
      );
    },
    popupContent: (row) => {
      const value = row.TotalReconciledRecords;
      return (
        <div className="relative ">
          <div
            className={`flex flex-col ${POPUP_CELL_MIN_WIDTH} transition-all ${BG_CLASS_NAME} ${TEXT_COLOR} text-14 rounded-lg px-[2px] py-[8px] mb-3 opacity-100`}
          >
            <div className="flex justify-between">
              <span className="font-semibold">
                {translate(
                  "reconciliation-runs.columns.TotalReconciledRecords"
                )}
              </span>
              <span className="font-semibold">{FormatWithCommas(value)}</span>
            </div>
          </div>
        </div>
      );
    },
  };
};

// In Transit Records Column
const buildInTransitRecordsColumn = (translate) => {
  return {
    accessorKey: "TotalInTransitRecords",
    header: translate("reconciliation-runs.columns.InTransitRecords"),
    size: COLUMN_SIZES.IN_TRANSIT_RECORDS,
    canFilter: false,
    truncate: true,
    processFilterValue: (value, row) => {
      return row.ReconciliationRunId.toString();
    },
    processFilterKey: (value, row) => {
      return "RunID";
    },
    showThousandSeperator: true,
    cellAction: (value, context) => {
      window.location.href = `/run-matching-records?run_id=${context.row.original.ReconciliationRunId}&stream_name=${context.row.original.ReconciliationStreamName}&type=InTransit&business_day=${context.row.original.BusinessDay}&business_day_instance=${context.row.original.BDInstance}`;
    },
    cellActionCheck: (value, context) =>
      value > ZERO_VALUE &&
      context.row.original.ReconciliationRunStatusName === "Completed",
  };
};

// Exception Records Column
const buildExceptionRecordsColumn = (translate) => {
  return {
    accessorKey: "TotalExceptionRecords",
    header: translate("reconciliation-runs.columns.ExceptionRecords"),
    size: COLUMN_SIZES.EXCEPTION_RECORDS,
    canFilter: false,
    truncate: true,
    processFilterValue: (value, row) => {
      return row.ReconciliationRunId;
    },
    processFilterKey: (value, row) => {
      return "RunID";
    },
    cellAction: (value, context) => {
      window.location.href = `/run-matching-records?run_id=${context.row.original.ReconciliationRunId}&stream_name=${context.row.original.ReconciliationStreamName}&type=Exception&business_day=${context.row.original.BusinessDay}&business_day_instance=${context.row.original.BDInstance}`;
    },
    cellActionCheck: (value, context) =>
      value > ZERO_VALUE &&
      context.row.original.ReconciliationRunStatusName === "Completed",
    showThousandSeperator: true,
  };
};

// Build all columns
export const buildColumns = (translate) => {
  return [
    buildRunStatusColumn(translate),
    buildStreamNameColumn(translate),
    buildTriggerColumn(translate),
    buildBusinessDayColumn(translate),
    buildStartDateColumn(translate),
    buildEndDateColumn(translate),
    buildMatchingRecordsColumn(translate),
    buildReconciledRecordsColumn(translate),
    buildInTransitRecordsColumn(translate),
    buildExceptionRecordsColumn(translate),
  ];
};
